import React, { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin, Search, PencilLine, Info } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { postcodePremises } from '../services/api';

export const EMPTY_ADDRESS = {
  organisation: '', line1: '', line2: '', line3: '', town: '', county: '', postcode: '', country: 'United Kingdom', uprn: '', source: '',
};

const PART_KEYS = ['line1', 'line2', 'line3', 'town', 'county', 'postcode', 'country'];

export const hasAddress = (p) => !!p && PART_KEYS.some((k) => k !== 'country' && (p[k] || '').trim());

// Multi-line text for PDFs / legacy `address` fields.
export const formatAddress = (p, sep = '\n') => {
  if (!p) return '';
  const lines = [p.line1, p.line2, p.line3, p.town, p.county, p.postcode, p.country];
  return lines.map((l) => (l || '').trim()).filter(Boolean).join(sep);
};

const FIELDS = [
  ['organisation', 'Company at this address (optional, not printed in the address)', 'e.g. Acme Ltd', 'sm:col-span-2'],
  ['line1', 'Unit / building', 'e.g. Unit 5, Mill House', 'sm:col-span-2'],
  ['line2', 'Street', 'e.g. High Street', 'sm:col-span-2'],
  ['line3', 'Locality (optional)', 'e.g. village or district', 'sm:col-span-2'],
  ['town', 'Town / city', 'e.g. town', ''],
  ['county', 'County (optional)', '', ''],
  ['postcode', 'Postcode', 'e.g. AB1 2CD', ''],
  ['country', 'Country', 'e.g. United Kingdom', ''],
];

const PostcodeResults = ({ data, testId, onPick }) => (
  <div className="mt-2 border border-slate-200 rounded-md bg-white" data-testid={`${testId}-results`}>
    {data.notice && (
      <div className={`flex gap-2 px-3 py-2 text-xs border-b ${data.premisesLevel ? 'text-slate-600 border-slate-100' : 'text-amber-800 bg-amber-50 border-amber-200'}`} data-testid={`${testId}-notice`}>
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" /> <span>{data.notice}</span>
      </div>
    )}
    {data.premisesLevel && data.addresses.length > 0 && (
      <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
        {data.addresses.length} address{data.addresses.length === 1 ? '' : 'es'} at {data.postcode}
      </div>
    )}
    <ul className="max-h-[45vh] sm:max-h-72 overflow-y-auto overscroll-contain divide-y divide-slate-100" role="listbox">
      {data.addresses.map((a, i) => (
        <li key={`${a.uprn || a.label}-${i}`}>
          <button
            type="button"
            onClick={() => onPick(a)}
            className="w-full text-left px-3 py-3 sm:py-2 text-sm hover:bg-slate-50 active:bg-slate-100 flex gap-2"
            data-testid={`${testId}-result-${i}`}
          >
            <MapPin className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
            <span>
              {a.organisation && <span className="block font-medium text-slate-900">{a.organisation}</span>}
              <span className={a.organisation ? 'text-slate-600' : 'text-slate-900'}>{a.label}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  </div>
);

/**
 * Shared UK address entry: postcode → list of every premises (Ideal Postcodes / PAF),
 * pick to fill structured fields, all fields editable, manual entry always available.
 * value: AddressParts; onChange(parts, picked?) — `picked` carries lat/lng of a chosen result.
 */
export const UkAddressFields = ({ value, onChange, testId = 'uk-address', legacyText = '' }) => {
  const parts = { ...EMPTY_ADDRESS, ...(value || {}) };
  const [query, setQuery] = useState(parts.postcode || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manual, setManual] = useState(hasAddress(value));
  const resultsRef = useRef(null);

  useEffect(() => { if (hasAddress(value)) setManual(true); }, [value]);
  useEffect(() => {
    if (data) setTimeout(() => resultsRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 50);
  }, [data]);

  const find = async () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true); setError(''); setData(null);
    try {
      setData(await postcodePremises(q));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Lookup failed — enter the address manually.');
    } finally {
      setLoading(false);
    }
  };

  const pick = (a) => {
    const next = { ...EMPTY_ADDRESS };
    Object.keys(EMPTY_ADDRESS).forEach((k) => { next[k] = a[k] || ''; });
    next.country = next.country || 'United Kingdom';
    onChange(next, a);
    setManual(true);
    setData(null);
    setQuery(next.postcode);
  };

  const set = (k, v) => onChange({ ...parts, [k]: v, source: parts.source === 'ideal_postcodes' ? 'ideal_postcodes_edited' : (parts.source || 'manual') });

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); find(); } }}
          placeholder="Postcode, e.g. M28 3PT"
          autoComplete="postal-code"
          inputMode="text"
          enterKeyHint="search"
          className="max-w-[12rem]"
          data-testid={`${testId}-postcode-search`}
        />
        <Button type="button" variant="outline" onClick={find} disabled={loading || !query.trim()} data-testid={`${testId}-find-btn`}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          <span className="ml-1.5">Find address</span>
        </Button>
      </div>
      {error && <div className="text-xs text-red-600" data-testid={`${testId}-error`}>{error}</div>}
      <div ref={resultsRef}>
        {data && <PostcodeResults data={data} testId={testId} onPick={pick} />}
        {data && data.addresses.length === 0 && (
          <div className="text-xs text-slate-500 mt-2" data-testid={`${testId}-no-results`}>
            No addresses found — use “Enter address manually” below.
          </div>
        )}
      </div>
      {!manual ? (
        <button type="button" onClick={() => setManual(true)} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid={`${testId}-manual-btn`}>
          <PencilLine className="h-3.5 w-3.5" /> Enter address manually
        </button>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid={`${testId}-fields`}>
          {FIELDS.map(([k, label, ph, cls]) => (
            <div key={k} className={cls}>
              <Label className="text-xs">{label}</Label>
              <Input
                value={parts[k] || ''}
                onChange={(e) => set(k, k === 'postcode' ? e.target.value.toUpperCase() : e.target.value)}
                placeholder={ph}
                className="mt-1"
                data-testid={`${testId}-${k}`}
              />
            </div>
          ))}
        </div>
      )}
      {legacyText && !hasAddress(value) && (
        <div className="text-xs text-slate-500 whitespace-pre-line" data-testid={`${testId}-legacy`}>
          Current saved address: {legacyText}
          <div className="text-[11px]">It stays as-is unless you look up or enter a new one.</div>
        </div>
      )}
    </div>
  );
};

export default UkAddressFields;
